import { useState, useEffect, useCallback } from 'react';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import { CATEGORY_OPTIONS, CURRENCIES, PAY_TYPES, GRANT_TYPES, EQUITY_GRANT_CATEGORIES, VARIABLE_DISPLAY_TYPES, EMPTY_COMPENSATION } from '../../constants';
import { saveCompensation, updateCycle } from '../../hooks/useFirestore';
import { formatCurrency, pctChange, statementTiming } from '../../utils/formatters';
import { computedTotalCash } from '../../utils/compensation';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../utils/cn';

export default function CompensationForm({ open, cycleId, compensation, totalComps, onClose }) {
  const toast = useToast();
  const isEdit = !!compensation?.id;
  const [form, setForm] = useState(EMPTY_COMPENSATION);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (compensation?.id) {
        setForm({
          ...EMPTY_COMPENSATION,
          ...compensation,
          totalCurrentComp: compensation.totalCurrentComp ?? computedTotalCash(compensation, 'current'),
          totalNewComp: compensation.totalNewComp ?? computedTotalCash(compensation, 'new'),
        });
      } else {
        setForm({ ...EMPTY_COMPENSATION });
      }
    }
  }, [open, compensation]);

  const set = useCallback((field, value) => setForm((f) => ({ ...f, [field]: value })), []);
  const setNum = useCallback((field, value) => setForm((f) => ({ ...f, [field]: value === '' ? '' : Number(value) || 0 })), []);

  /* Equity grant helpers */
  const addGrant = () =>
    setForm((f) => ({
      ...f,
      equityGrants: [...f.equityGrants, { type: 'RSU', grantCategory: 'refresh', units: 0, vestingPeriod: '4 years', cliff: '1 year' }],
    }));
  const removeGrant = (i) => setForm((f) => ({ ...f, equityGrants: f.equityGrants.filter((_, idx) => idx !== i) }));
  const updateGrant = (i, field, val) =>
    setForm((f) => ({
      ...f,
      equityGrants: f.equityGrants.map((g, idx) => (idx === i ? { ...g, [field]: val } : g)),
    }));

  const totalCurrent = Number(form.totalCurrentComp) || 0;
  const totalNew = Number(form.totalNewComp) || 0;

  const handleSave = async () => {
    if (!form.employeeName.trim() || !form.employeeEmail.trim()) {
      toast.error('Employee name and email are required');
      return;
    }
    if (!form.communicatorEmail.trim()) {
      toast.error('Communicator email is required');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...form,
        currentBase: Number(form.currentBase) || 0,
        newBase: Number(form.newBase) || 0,
        currentVariableTarget: Number(form.currentVariableTarget) || 0,
        newVariableTarget: Number(form.newVariableTarget) || 0,
        totalCurrentComp: Number(form.totalCurrentComp) || 0,
        totalNewComp: Number(form.totalNewComp) || 0,
        equityGrants: form.equityGrants.map((g) => ({
          ...g,
          units: Number(g.units) || 0,
        })),
      };
      // Remove `id` from data if editing
      delete data.id;

      await saveCompensation(cycleId, compensation?.id || null, data);
      if (!isEdit) {
        await updateCycle(cycleId, { compCount: (totalComps || 0) + 1 });
      }
      toast.success(isEdit ? 'Compensation updated' : 'Compensation added');
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Compensation' : 'New Compensation'}
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>{isEdit ? 'Save Changes' : 'Add Compensation'}</Button>
        </div>
      }
    >
      <div className="space-y-8">
        {/* Section: Employee */}
        <Section title="Employee Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Employee Name" placeholder="Jane Doe" value={form.employeeName} onChange={(e) => set('employeeName', e.target.value)} />
            <Input label="Employee Email" placeholder="jane@forter.com" type="email" value={form.employeeEmail} onChange={(e) => set('employeeEmail', e.target.value)} />
            <Input label="Department" placeholder="Engineering" value={form.department} onChange={(e) => set('department', e.target.value)} />
            <Input label="Current Title" placeholder="Senior Engineer" value={form.currentTitle} onChange={(e) => set('currentTitle', e.target.value)} />
          </div>
        </Section>

        {/* Section: Communicator */}
        <Section title="Communicator">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Communicator Email" placeholder="manager@forter.com" type="email" value={form.communicatorEmail} onChange={(e) => set('communicatorEmail', e.target.value)} />
            <Input label="Communicator Name" placeholder="John Smith" value={form.communicatorName} onChange={(e) => set('communicatorName', e.target.value)} />
          </div>
        </Section>

        {/* Section: Category & Role */}
        <Section title="Category & Role">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select label="Category" options={CATEGORY_OPTIONS} value={form.category} onChange={(e) => set('category', e.target.value)} />
            <Input label="New Title" placeholder="Staff Engineer (if changed)" value={form.newTitle} onChange={(e) => set('newTitle', e.target.value)} />
            <Input label="Effective Date" type="date" value={form.effectiveDate} onChange={(e) => set('effectiveDate', e.target.value)} />
            <Input label="Pay Date" placeholder="e.g. Oct 30, 2026 paycheck" value={form.payDate || ''} onChange={(e) => set('payDate', e.target.value)} />
          </div>
          {statementTiming(form.effectiveDate, form.payDate).length > 0 && (
            <p className="mt-2 text-xs text-navy-400">
              Statement will say: <span className="text-navy-600">{statementTiming(form.effectiveDate, form.payDate).join(' ')}</span>
            </p>
          )}
        </Section>

        {/* Section: Base Compensation */}
        <Section title="Base Compensation">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select label="Currency" options={CURRENCIES.map((c) => ({ value: c.value, label: `${c.value} (${c.symbol})` }))} value={form.currency} onChange={(e) => set('currency', e.target.value)} />
            <Select label="Pay Type" options={PAY_TYPES} value={form.payType} onChange={(e) => set('payType', e.target.value)} />
            <Input label="Current Base" type="number" placeholder="0" value={form.currentBase || ''} onChange={(e) => setNum('currentBase', e.target.value)} />
            <Input label="New Base" type="number" placeholder="0" value={form.newBase || ''} onChange={(e) => setNum('newBase', e.target.value)} />
          </div>
          {form.currentBase > 0 && form.newBase > 0 && (
            <ComparisonPreview label="Base" current={form.currentBase} next={form.newBase} currency={form.currency} />
          )}
        </Section>

        {/* Section: Variable Compensation */}
        <Section title="Variable / Bonus Target">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select label="Display Type" options={VARIABLE_DISPLAY_TYPES} value={form.variableDisplayType || 'amount'} onChange={(e) => set('variableDisplayType', e.target.value)} />
            <Input label="Current Target" type="number" placeholder="0" value={form.currentVariableTarget || ''} onChange={(e) => setNum('currentVariableTarget', e.target.value)} />
            <Input label="New Target" type="number" placeholder="0" value={form.newVariableTarget || ''} onChange={(e) => setNum('newVariableTarget', e.target.value)} />
          </div>
        </Section>

        {/* Section: Total Cash */}
        <Section title="Total Cash">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Current Total Cash" type="number" placeholder="0" value={form.totalCurrentComp || ''} onChange={(e) => setNum('totalCurrentComp', e.target.value)} />
            <Input label="New Total Cash" type="number" placeholder="0" value={form.totalNewComp || ''} onChange={(e) => setNum('totalNewComp', e.target.value)} />
          </div>
        </Section>

        {/* Section: Equity Grants */}
        <Section title="Equity Grants">
          {form.equityGrants.length > 0 && (
            <div className="space-y-4 mb-4">
              {form.equityGrants.map((grant, i) => (
                <div key={i} className="bg-surface-50 rounded-xl border border-surface-200 p-4 relative">
                  <button
                    onClick={() => removeGrant(i)}
                    className="absolute top-3 right-3 p-1 rounded-lg hover:bg-red-50 text-navy-300 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Select label="Category" options={EQUITY_GRANT_CATEGORIES} value={grant.grantCategory || 'refresh'} onChange={(e) => updateGrant(i, 'grantCategory', e.target.value)} />
                    <Select label="Type" options={GRANT_TYPES} value={grant.type} onChange={(e) => updateGrant(i, 'type', e.target.value)} />
                    <Input label="Shares" type="number" value={grant.units || ''} onChange={(e) => updateGrant(i, 'units', Number(e.target.value) || 0)} />
                    <Input label="Cliff" placeholder="1 year" value={grant.cliff || ''} onChange={(e) => updateGrant(i, 'cliff', e.target.value)} />
                    <Input label="Vesting" placeholder="4 years" value={grant.vestingPeriod || ''} onChange={(e) => updateGrant(i, 'vestingPeriod', e.target.value)} />
                  </div>
                  <div className="mt-3">
                    <Input 
                      label="Custom Vesting Text" 
                      placeholder="Optional - overrides cliff/vesting if set (e.g., 'Vest immediately over 4 year period...')" 
                      value={grant.vestingText || ''} 
                      onChange={(e) => updateGrant(i, 'vestingText', e.target.value)} 
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button variant="secondary" size="sm" onClick={addGrant}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
            Add Grant
          </Button>
        </Section>

        {/* Section: Notes */}
        <Section title="Notes">
          <Textarea label="Internal Notes (admin only)" placeholder="Internal notes…" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Section>

        {/* Summary */}
        <div className="bg-gradient-to-r from-accent-50 to-cyan-50 rounded-2xl border border-accent-200/50 p-5">
          <h4 className="text-sm font-bold text-navy-900 mb-3">Compensation Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-navy-400 mb-1">Current Total</p>
              <p className="text-lg font-bold text-navy-800">{formatCurrency(totalCurrent, form.currency)}</p>
            </div>
            <div>
              <p className="text-xs text-navy-400 mb-1">New Total</p>
              <p className="text-lg font-bold text-accent-700">{formatCurrency(totalNew, form.currency)}</p>
            </div>
            <div>
              <p className="text-xs text-navy-400 mb-1">Change</p>
              <p className={cn('text-lg font-bold', totalNew >= totalCurrent ? 'text-emerald-600' : 'text-red-500')}>
                {pctChange(totalCurrent, totalNew)}
              </p>
            </div>
          </div>
        </div>

      </div>
    </Modal>
  );
}

/* ── Helpers ── */

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-navy-900 mb-3 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function ComparisonPreview({ current, next, currency }) {
  const max = Math.max(current, next);
  return (
    <div className="mt-3 bg-surface-50 rounded-xl p-4 space-y-2.5">
      <div className="flex items-center gap-3">
        <span className="text-xs text-navy-400 w-16">Current</span>
        <div className="flex-1 h-6 bg-surface-200 rounded-full overflow-hidden">
          <div className="h-full bg-navy-300 rounded-full transition-all duration-700" style={{ width: `${(current / max) * 100}%` }} />
        </div>
        <span className="text-xs font-semibold text-navy-600 w-24 text-right">{formatCurrency(current, currency)}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-navy-400 w-16">New</span>
        <div className="flex-1 h-6 bg-surface-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-accent-500 to-cyan-500 rounded-full transition-all duration-700" style={{ width: `${(next / max) * 100}%` }} />
        </div>
        <span className="text-xs font-semibold text-accent-700 w-24 text-right">{formatCurrency(next, currency)}</span>
      </div>
      <p className={cn('text-xs font-semibold text-right', next >= current ? 'text-emerald-600' : 'text-red-500')}>
        {pctChange(current, next)}
      </p>
    </div>
  );
}
